import { type ReactNode, useMemo, useState } from 'react'
import type { IconType } from 'react-icons'
import {
  PiAddressBook,
  PiBellRinging,
  PiBuildings,
  PiCalendarPlus,
  PiChartLineUp,
  PiClock,
  PiClockCounterClockwise,
  PiDotsSixVertical,
  PiDownloadSimple,
  PiEnvelope,
  PiFileText,
  PiGear,
  PiHandCoins,
  PiHandshake,
  PiHouse,
  PiListBullets,
  PiMegaphone,
  PiNotePencil,
  PiPencilSimple,
  PiPhone,
  PiPushPin,
  PiSealPercent,
  PiX,
} from 'react-icons/pi'

// eslint-disable-next-line no-restricted-imports -- approved exception: Phosphor's ellipsis renders too thin to be legible in-app, so Ellipsis uses lucide
import { Ellipsis } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  TargetArrowOutlineIcon,
  WorkspaceOutlineIcon,
} from '../Layout/LeftNav/nav-icons'
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ActionsDropdown } from '../Buttons/ActionsDropdown'
import ReminderModal from '../Reminders/ReminderModal'
import { Dialog, DialogContent, DialogTitle, FbModal } from '../UI/Modal'
import { cn } from '../UI/cn'
import { useAuth } from 'context/authentication/useAuth'
import {
  type PinListItem,
  type RecentListItem,
  useDeleteHomepagePin,
  useListHomepagePins,
  useListHomepageRecents,
  useReorderHomepagePins,
} from 'services/homepage'
import { type ReminderList, useListReminders } from 'services/reminders'

/**
 * Quick Access card — Homepage Basics: Pins & Recents
 *
 * Sits above the performance dashboard on the homepage. Three sections in one
 * card:
 *   1. Pinned       — horizontally scrolling cards (Notion "Recently visited" feel)
 *   2. Recently visited — vertically scrolling list (Figma "My Watchlist" pattern)
 *   3. Upcoming     — upcoming reminders (title + next run time + recurrence),
 *                     NOT calendar events. Mirrors the `ReminderListItem` shape.
 *
 * All styling uses the ds2026-* design tokens so it matches the Figma design
 * system 1:1. Data is wired to the real homepage pin/recent APIs and the
 * reminders API (see `pinFromApi` / `recentFromApi` / `upcomingFromApi`).
 */

// Border + tighter shadow matching the "My Summary" FbDropdown panel, so these
// menus stand out a touch more than the default borderless ActionsDropdown.
const MENU_CONTENT_CLASS =
  '!border !border-line !shadow-[0px_4px_4px_rgba(16,24,40,0.10),0px_2px_2px_rgba(16,24,40,0.06)]'

// Shared trigger style for the section three-dot menus (Pins, Upcoming): small
// square icon button that reverts to neutral on release.
const MENU_TRIGGER_CLASS =
  'size-8 rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-500'

// Icon component shape shared by react-icons and our custom nav SVGs.
type IconComponent = React.ComponentType<{ size?: number; className?: string }>

// ─── Entity types + per-type icon/tint (calm, not rainbow) ─────────────────

// Records are the pinnable entities. company/deal/campaign deep-link to a
// detail page; contact/note have no full page, so pinning them pops a card
// (see ContactCard / NoteCard). Each gets a colored chip.
type EntityType =
  | 'company'
  | 'deal'
  | 'campaign'
  | 'contact'
  | 'note'
  | 'document'

type EntityMeta = {
  icon: IconComponent
  label: string
  iconClass: string
  tintClass: string
  gradientClass: string
}

const ENTITY_META: Record<EntityType, EntityMeta> = {
  company: {
    icon: PiBuildings,
    label: 'Company',
    iconClass: 'text-ds2026-badge-primary-icon',
    tintClass: 'bg-ds2026-badge-primary-bg',
    gradientClass: 'from-ds2026-badge-primary-bg',
  },
  deal: {
    icon: PiHandshake,
    label: 'Deal',
    iconClass: 'text-ds2026-badge-sky-icon',
    tintClass: 'bg-ds2026-badge-sky-bg',
    gradientClass: 'from-ds2026-badge-sky-bg',
  },
  campaign: {
    icon: PiMegaphone,
    label: 'Campaign',
    iconClass: 'text-ds2026-badge-orange-icon',
    tintClass: 'bg-ds2026-badge-orange-bg',
    gradientClass: 'from-ds2026-badge-orange-bg',
  },
  contact: {
    icon: PiAddressBook,
    label: 'Contact',
    iconClass: 'text-ds2026-badge-violet-icon',
    tintClass: 'bg-ds2026-badge-violet-bg',
    gradientClass: 'from-ds2026-badge-violet-bg',
  },
  note: {
    icon: PiNotePencil,
    label: 'Note',
    iconClass: 'text-ds2026-badge-warning-icon',
    tintClass: 'bg-ds2026-badge-warning-bg',
    gradientClass: 'from-ds2026-badge-warning-bg',
  },
  document: {
    icon: PiFileText,
    label: 'Document',
    iconClass: 'text-ds2026-badge-cyan-icon',
    tintClass: 'bg-ds2026-badge-cyan-bg',
    gradientClass: 'from-ds2026-badge-cyan-bg',
  },
}

// ─── Visited-page icons (mirror the left nav) ───────────────────────────────

// Recents that are section / list pages reuse the matching left-nav icon, so a
// visited "Settings → Sales Stages" row carries the gear, the dashboard carries
// the performance chart, etc. Neutral gray chip keeps them visually distinct
// from the colored record chips above. Keys mirror the nav sections in
// `useNavItems` — keep in sync if the nav gains/loses a section.
type NavPageKey =
  | 'home'
  | 'targets'
  | 'campaigns'
  | 'contacts'
  | 'samples'
  | 'rebates'
  | 'deals'
  | 'performance'
  | 'workspace'
  | 'documents'
  | 'reminders'
  | 'settings'

type PageMeta = { icon: IconComponent; label: string }

const NAV_PAGE_META: Record<NavPageKey, PageMeta> = {
  home: { icon: PiHouse, label: 'Home' },
  targets: { icon: TargetArrowOutlineIcon, label: 'Targets' },
  campaigns: { icon: PiMegaphone, label: 'Campaigns' },
  contacts: { icon: PiAddressBook, label: 'Contacts' },
  samples: { icon: PiHandCoins, label: 'Samples' },
  rebates: { icon: PiSealPercent, label: 'Rebates' },
  deals: { icon: PiHandshake, label: 'Deals' },
  performance: { icon: PiChartLineUp, label: 'Performance' },
  workspace: { icon: WorkspaceOutlineIcon, label: 'Workspace' },
  documents: { icon: PiFileText, label: 'Documents' },
  reminders: { icon: PiBellRinging, label: 'Reminders' },
  settings: { icon: PiGear, label: 'Settings' },
}

const PAGE_CHIP = {
  iconClass: 'text-ds2026-badge-gray-icon',
  tintClass: 'bg-ds2026-badge-gray-bg',
  gradientClass: 'from-ds2026-badge-gray-bg',
}

// Pages that mirror a record type borrow that record's chip color so the two
// read as the same family in the list (Contacts → violet, Deals → sky). Every
// other page falls back to the neutral PAGE_CHIP.
const PAGE_ENTITY_CHIP: Partial<Record<NavPageKey, EntityType>> = {
  contacts: 'contact',
  deals: 'deal',
}

// ─── View-model types ───────────────────────────────────────────────────────

// contact / note / document pins carry the detail shown in their popup card
// (they have no full page to navigate to). company/deal/campaign pins deep-link.
type ContactDetail = {
  role: string
  company: string
  email: string
  phone: string
  lastContacted: string
}
type NoteDetail = {
  relatedTo: string
  author: string
  date: string
  body: string
}
// Mirrors the `Document` model (client/src/types/documents.ts): full name,
// extension, size, who uploaded it, and the download URL.
type DocumentDetail = {
  fileExtension: string
  size: string
  uploadedBy: string
  uploadedOn: string
  fileUrl: string
}
// A pin is either a record (company/deal/…) or a whole page (Documents,
// Settings, …). Page pins carry a NavPageKey and render with the matching
// left-nav icon; clicking one would deep-link to that page in the real app.
type PinItem = {
  id: string
  type: EntityType | 'page'
  title: string
  page?: NavPageKey
  // Deep-link target for record pins / page pins that navigate to a full page.
  // Comes straight from the API (`notification_link()` for records, the page
  // registry href for pages) — navigate it verbatim, never rebuild it.
  href?: string
  contact?: ContactDetail
  note?: NoteDetail
  document?: DocumentDetail
}

// Resolves the chip + label for a pin, whether it's a record or a page.
function pinMeta(pin: PinItem): {
  icon: IconComponent
  label: string
  iconClass: string
  tintClass: string
  gradientClass: string
} {
  if (pin.type === 'page') {
    const page = pin.page ?? 'home'
    const borrowed = PAGE_ENTITY_CHIP[page]
    const chip = borrowed ? ENTITY_META[borrowed] : PAGE_CHIP
    return {
      icon: NAV_PAGE_META[page].icon,
      label: 'Page',
      iconClass: chip.iconClass,
      tintClass: chip.tintClass,
      gradientClass: chip.gradientClass,
    }
  }
  return ENTITY_META[pin.type]
}

// ─── API → view-model mapping ────────────────────────────────────────────────

// The API `page_key` (e.g. "workspace.documents", "settings") mirrors
// server `PAGE_REGISTRY`. Map it to the local NavPageKey so page pins/recents
// render with the matching left-nav icon. Unknown keys fall back to 'home'.
function navPageKeyFromPageKey(pageKey: string | null): NavPageKey {
  switch (pageKey) {
    case 'workspace.documents':
      return 'documents'
    case 'workspace.reminders':
      return 'reminders'
    case 'settings':
      return 'settings'
    case 'workspace.products':
    case 'workspace.brands':
    case 'workspace.activity':
      return 'workspace'
    default:
      return 'home'
  }
}

// The API target_type union matches our EntityType plus 'page'. It's typed as a
// bare string on the wire, so narrow it here before assigning to PinItem.type.
function pinType(targetType: string): EntityType | 'page' {
  switch (targetType) {
    case 'company':
    case 'deal':
    case 'campaign':
    case 'contact':
    case 'note':
    case 'document':
    case 'page':
      return targetType
    default:
      return 'page'
  }
}

// Short relative-time formatter consistent with the old mock strings
// ("9m ago", "1h ago", "Yesterday", "May 28").
function relativeWhen(iso: string): string {
  const then = new Date(iso)
  const diffMs = Date.now() - then.getTime()
  const diffMin = Math.round(diffMs / 60_000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.round(diffHr / 24)
  if (diffDay === 1) return 'Yesterday'
  if (diffDay < 7) return `${diffDay}d ago`
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// Short date label for upcoming reminders ("Today" / "Tomorrow" / "Wed, Jun 3").
function upcomingWhen(date: Date): string {
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const dayDiff = Math.round(
    (startOfDay(date) - startOfDay(new Date())) / 86_400_000
  )
  if (dayDiff === 0) return 'Today'
  if (dayDiff === 1) return 'Tomorrow'
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

function upcomingTime(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

// Recurring reminders carry a humanized recurrence label; one-off reminders
// omit it (UpcomingRow renders "One-time" for the empty case).
const RECURRENCE_LABEL: Partial<
  Record<NonNullable<ReminderList['reminder_type']>, string>
> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
  custom: 'Custom',
}

function pinFromApi(item: PinListItem): PinItem {
  const type = pinType(item.target_type)
  const card = item.card ?? {}
  const pin: PinItem = {
    id: String(item.id),
    type,
    title: item.label,
    href: item.href ?? undefined,
  }
  if (type === 'page') {
    pin.page = navPageKeyFromPageKey(item.page_key)
  } else if (type === 'contact') {
    pin.contact = {
      role: card.role ?? '',
      company: card.company ?? '',
      email: card.email ?? '',
      phone: card.phone ?? '',
      lastContacted: card.last_contacted ?? '',
    }
  } else if (type === 'note') {
    pin.note = {
      relatedTo: card.related_to ?? item.label,
      author: card.author ?? '',
      date: card.created ?? '',
      body: card.content ?? '',
    }
  } else if (type === 'document') {
    pin.document = {
      fileExtension: card.file_extension ?? '',
      size: card.size ?? '',
      uploadedBy: card.uploaded_by ?? '',
      uploadedOn: card.uploaded_on ?? '',
      fileUrl: card.download_url ?? '',
    }
  }
  return pin
}

function recentFromApi(item: RecentListItem): RecentItem | null {
  const base = {
    id: String(item.id),
    title: item.label,
    when: relativeWhen(item.visited_at),
    href: item.href ?? undefined,
  }
  if (item.target_type === 'page') {
    return { ...base, page: navPageKeyFromPageKey(item.page_key) }
  }
  // Records that have a colored entity chip + a full page (company/deal/
  // campaign). contact/note/document have no navigable page in recents —
  // visit tracking never records those types, so skip them defensively.
  if (
    item.target_type === 'company' ||
    item.target_type === 'deal' ||
    item.target_type === 'campaign'
  ) {
    return { ...base, entity: item.target_type }
  }
  return null
}

function upcomingFromApi(reminder: ReminderList): ReminderItem {
  const iso = reminder.next_run_at ?? reminder.run_at
  const date = iso ? new Date(iso) : null
  return {
    id: String(reminder.id),
    title: reminder.title,
    when: date ? upcomingWhen(date) : '',
    time: date ? upcomingTime(date) : '',
    recurrence: reminder.reminder_type
      ? RECURRENCE_LABEL[reminder.reminder_type]
      : undefined,
  }
}

// Recents = recently *visited pages*. A row is either a visited record (colored
// entity chip) or a visited section/list page (neutral chip with the matching
// left-nav icon).
type RecentItem = {
  id: string
  title: string
  subtitle?: string
  when: string
  // Navigation target from the API (`notification_link()` for records, the
  // page registry href for pages). Navigate it verbatim on row click.
  href?: string
} & (
  | { entity: Extract<EntityType, 'company' | 'deal' | 'campaign'> }
  | { page: NavPageKey }
)

// Mirrors `ReminderListItem`: reminders have a title, a next run time, and a
// recurrence type. They are NOT calendar events — there's no "done" state.
type ReminderItem = {
  id: string
  title: string
  // Humanized `next_run_at`
  when: string
  time: string
  // Recurrence label derived from `reminder_type`; omitted for one-off reminders
  recurrence?: string
}

// ─── Shared sub-components ───────────────────────────────────────────────────

function SectionHeader({
  icon: Icon,
  title,
  menu,
}: {
  icon: IconType
  title: string
  menu?: ReactNode
}) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon className="text-ds2026-text-secondary" size={16} />
        <h3 className="text-sm font-semibold text-ds2026-text-title">
          {title}
        </h3>
      </div>
      {menu}
    </div>
  )
}

// Muted inline "nothing yet" label — rides on the header's right slot (where
// the three-dot menu sits once the section has content).
function EmptyLabel({ text }: { text: string }) {
  return <span className="text-xs text-ds2026-text-secondary">{text}</span>
}

// One section rendered as a compact column for the all-empty 3-column row:
// icon + title + muted label grouped on the left. Vertical dividers between
// columns do the visual separating.
function EmptyColumn({
  icon: Icon,
  title,
  label,
}: {
  icon: IconType
  title: string
  label: string
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <Icon className="shrink-0 text-ds2026-text-secondary" size={16} />
      <h3 className="shrink-0 text-sm font-semibold text-ds2026-text-title">
        {title}
      </h3>
      <span className="truncate text-xs text-ds2026-text-secondary">
        {label}
      </span>
    </div>
  )
}

// When every section is empty, collapse the whole card to a single compact
// 3-column row so it doesn't take up a full card's worth of vertical space.
function AllEmptyRow() {
  return (
    <div className="flex flex-col gap-y-4 md:flex-row md:items-stretch md:gap-y-0 md:py-1">
      <div className="flex min-w-0 flex-1 md:pr-6">
        <EmptyColumn icon={PiPushPin} title="Pinned" label="No pins yet" />
      </div>
      <div className="hidden w-px self-stretch bg-ds2026-border-tertiary md:block" />
      <div className="flex min-w-0 flex-1 md:px-6">
        <EmptyColumn
          icon={PiClockCounterClockwise}
          title="Recently visited"
          label="Nothing here yet"
        />
      </div>
      <div className="hidden w-px self-stretch bg-ds2026-border-tertiary md:block" />
      <div className="flex min-w-0 flex-1 md:pl-6">
        <EmptyColumn
          icon={PiBellRinging}
          title="Upcoming"
          label="Nothing here yet"
        />
      </div>
    </div>
  )
}

function PinCard({ pin, onClick }: { pin: PinItem; onClick: () => void }) {
  const meta = pinMeta(pin)
  const Icon = meta.icon
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative flex h-[108px] w-[172px] shrink-0 flex-col justify-between overflow-hidden rounded-xl border border-ds2026-border-primary bg-ds2026-card-bg p-3 text-left transition-shadow hover:shadow-md"
    >
      {/* Soft gradient wash at the top edge (Notion feel) — purely decorative */}
      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b to-transparent opacity-60',
          meta.gradientClass
        )}
      />
      {/* Small icon chip, top-right corner — clear of the title */}
      <span
        className={cn(
          'absolute right-2.5 top-2.5 flex size-7 items-center justify-center rounded-lg',
          meta.tintClass
        )}
      >
        <Icon className={meta.iconClass} size={15} />
      </span>
      {/* Title — padded on the right so it never runs under the icon */}
      <p className="relative line-clamp-2 pr-8 text-sm font-medium leading-snug text-ds2026-text-title">
        {pin.title}
      </p>
      <span className="relative text-xxs font-medium uppercase tracking-wide text-ds2026-text-secondary">
        {meta.label}
      </span>
    </button>
  )
}

function RecentRow({
  item,
  onClick,
}: {
  item: RecentItem
  onClick: () => void
}) {
  // Records get their colored entity chip. Visited pages use the matching
  // left-nav icon; pages that mirror a record type (see PAGE_ENTITY_CHIP)
  // borrow that record's color, the rest get the neutral PAGE_CHIP.
  let Icon: IconComponent
  let iconClass: string
  let tintClass: string
  if ('entity' in item) {
    ;({ icon: Icon, iconClass, tintClass } = ENTITY_META[item.entity])
  } else {
    Icon = NAV_PAGE_META[item.page].icon
    const borrowed = PAGE_ENTITY_CHIP[item.page]
    iconClass = borrowed ? ENTITY_META[borrowed].iconClass : PAGE_CHIP.iconClass
    tintClass = borrowed ? ENTITY_META[borrowed].tintClass : PAGE_CHIP.tintClass
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 border-b border-ds2026-border-secondary px-1 py-3 text-left transition-colors hover:bg-ds2026-bg-gray-primary"
    >
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-lg',
          tintClass
        )}
      >
        <Icon className={iconClass} size={18} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-ds2026-text-title">
          {item.title}
        </span>
        {item.subtitle && (
          <span className="truncate text-xs text-ds2026-text-secondary">
            {item.subtitle}
          </span>
        )}
      </span>
      <span className="shrink-0 text-xs text-ds2026-text-secondary">
        {item.when}
      </span>
    </button>
  )
}

function UpcomingRow({ reminder }: { reminder: ReminderItem }) {
  // Recurring reminders get an accented badge; one-off reminders read "One-time".
  const isRecurring = Boolean(reminder.recurrence)
  // NOTE: built as a plain template literal, NOT cn(). tailwind-merge doesn't
  // recognize the custom `text-xxs` size and would drop it when merged next to
  // a `text-*` color class, leaving the badge at the inherited 16px.
  const badgeColor = isRecurring
    ? 'bg-ds2026-badge-sky-bg text-ds2026-badge-sky-text'
    : 'bg-ds2026-badge-gray-bg text-ds2026-badge-gray-text'
  return (
    <div className="flex items-center gap-3 border-b border-ds2026-border-secondary px-1 py-3 last:border-b-0">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span
          className={`w-fit rounded px-1.5 py-0.5 text-xxs font-medium leading-none ${badgeColor}`}
        >
          {reminder.recurrence ?? 'One-time'}
        </span>
        <span className="truncate text-sm font-medium text-ds2026-text-body">
          {reminder.title}
        </span>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <span className="text-xs text-ds2026-text-secondary">
          {reminder.when}
        </span>
        <span className="text-sm font-medium text-ds2026-text-body">
          {reminder.time}
        </span>
      </div>
    </div>
  )
}

// ─── Pinned contact / note popup cards ──────────────────────────────────────
//
// contact & note pins have no full page, so clicking them pops a compact card.
// Style follows the Figma "Deal Card" (rounded-2xl, soft border + shadow) but
// trimmed down — no sales-stage footer, just the essentials.

function CardMetaRow({
  icon: Icon,
  text,
}: {
  icon: IconComponent
  text: string
}) {
  return (
    <div className="flex items-center gap-2.5 text-sm text-ds2026-text-secondary">
      <Icon className="shrink-0 text-ds2026-text-secondary" size={16} />
      <span className="truncate">{text}</span>
    </div>
  )
}

function CardShell({
  icon: Icon,
  iconClass,
  tintClass,
  title,
  subtitle,
  onClose,
  children,
  // Documents show their full (often long) file name, so the title wraps
  // instead of truncating to a single line like the contact/note cards.
  wrapTitle = false,
}: {
  icon: IconComponent
  iconClass: string
  tintClass: string
  title: string
  subtitle?: string
  onClose: () => void
  children: ReactNode
  wrapTitle?: boolean
}) {
  return (
    <div className="flex w-full flex-col gap-3 rounded-2xl border border-ds2026-border-primary bg-ds2026-card-bg p-4 shadow-lg">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className={cn(
              'flex size-10 shrink-0 items-center justify-center rounded-lg',
              tintClass
            )}
          >
            <Icon className={iconClass} size={20} />
          </span>
          <div className="flex min-w-0 flex-col">
            <p
              className={cn(
                'text-lg font-semibold leading-tight text-ds2026-text-title',
                wrapTitle ? 'break-words' : 'truncate'
              )}
            >
              {title}
            </p>
            {subtitle && (
              <p className="truncate text-sm font-medium text-ds2026-text-secondary">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-ds2026-text-secondary transition-colors hover:bg-ds2026-bg-gray-primary"
        >
          <PiX size={18} />
        </button>
      </div>
      <div className="flex flex-col gap-2 border-t border-ds2026-border-secondary pt-3">
        {children}
      </div>
    </div>
  )
}

function ContactCard({
  name,
  detail,
  onClose,
}: {
  name: string
  detail: ContactDetail
  onClose: () => void
}) {
  return (
    <CardShell
      icon={ENTITY_META.contact.icon}
      iconClass={ENTITY_META.contact.iconClass}
      tintClass={ENTITY_META.contact.tintClass}
      title={name}
      subtitle={
        [detail.role, detail.company].filter(Boolean).join(' · ') || undefined
      }
      onClose={onClose}
    >
      {detail.email && <CardMetaRow icon={PiEnvelope} text={detail.email} />}
      {detail.phone && <CardMetaRow icon={PiPhone} text={detail.phone} />}
      {detail.lastContacted && (
        <CardMetaRow
          icon={PiClock}
          text={`Last contacted ${detail.lastContacted}`}
        />
      )}
    </CardShell>
  )
}

function NoteCard({
  title,
  detail,
  onClose,
}: {
  title: string
  detail: NoteDetail
  onClose: () => void
}) {
  return (
    <CardShell
      icon={ENTITY_META.note.icon}
      iconClass={ENTITY_META.note.iconClass}
      tintClass={ENTITY_META.note.tintClass}
      title={title}
      subtitle={detail.relatedTo || undefined}
      onClose={onClose}
    >
      <p className="whitespace-pre-line text-sm leading-relaxed text-ds2026-text-body">
        {detail.body}
      </p>
      {[detail.author, detail.date].some(Boolean) && (
        <CardMetaRow
          icon={PiClock}
          text={[detail.author, detail.date].filter(Boolean).join(' · ')}
        />
      )}
    </CardShell>
  )
}

function DocumentCard({
  name,
  detail,
  onClose,
}: {
  name: string
  detail: DocumentDetail
  onClose: () => void
}) {
  return (
    <CardShell
      icon={ENTITY_META.document.icon}
      iconClass={ENTITY_META.document.iconClass}
      tintClass={ENTITY_META.document.tintClass}
      title={name}
      subtitle={
        [detail.fileExtension, detail.size].filter(Boolean).join(' · ') ||
        'Document'
      }
      onClose={onClose}
      wrapTitle
    >
      {[detail.uploadedBy, detail.uploadedOn].some(Boolean) && (
        <CardMetaRow
          icon={PiClock}
          text={
            detail.uploadedBy
              ? `Uploaded by ${[detail.uploadedBy, detail.uploadedOn].filter(Boolean).join(' · ')}`
              : `Uploaded ${detail.uploadedOn}`
          }
        />
      )}
      {detail.fileUrl && (
        <a
          href={detail.fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-sm font-medium text-brand-active transition-colors hover:text-basil-800"
        >
          <PiDownloadSimple size={16} />
          Download
        </a>
      )}
    </CardShell>
  )
}

// Bare popup: the card supplies its own chrome, so the dialog shell is stripped
// of padding/border/background. Hidden DialogTitle keeps it accessible.
function PinCardPopup({
  pin,
  onClose,
}: {
  pin: PinItem | null
  onClose: () => void
}) {
  return (
    <Dialog open={pin !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        noPadding
        disableCloseIcon
        className="!max-w-[400px] !gap-0 !border-0 !bg-transparent !p-0 !shadow-none"
      >
        <DialogTitle className="sr-only">{pin?.title}</DialogTitle>
        {pin?.type === 'contact' && pin.contact && (
          <ContactCard
            name={pin.title}
            detail={pin.contact}
            onClose={onClose}
          />
        )}
        {pin?.type === 'note' && pin.note && (
          <NoteCard title={pin.title} detail={pin.note} onClose={onClose} />
        )}
        {pin?.type === 'document' && pin.document && (
          <DocumentCard
            name={pin.title}
            detail={pin.document}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

// ─── Edit Pins modal (drag-to-reorder + remove) ──────────────────────────────

function SortablePinRow({
  pin,
  onRemove,
}: {
  pin: PinItem
  onRemove: () => void
}) {
  const meta = pinMeta(pin)
  const Icon = meta.icon
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pin.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2.5 rounded-lg border border-ds2026-border-primary bg-ds2026-card-bg p-2.5',
        isDragging && 'z-10 shadow-md'
      )}
    >
      {/* Drag handle */}
      <button
        type="button"
        aria-label={`Drag to reorder ${pin.title}`}
        className="flex size-7 shrink-0 cursor-grab items-center justify-center rounded-md text-ds2026-text-secondary transition-colors hover:bg-ds2026-bg-gray-primary active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <PiDotsSixVertical size={18} />
      </button>
      {/* Type icon chip */}
      <span
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg',
          meta.tintClass
        )}
      >
        <Icon className={meta.iconClass} size={16} />
      </span>
      {/* Title + type label */}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-ds2026-text-title">
          {pin.title}
        </span>
        <span className="text-xxs font-medium uppercase tracking-wide text-ds2026-text-secondary">
          {meta.label}
        </span>
      </span>
      {/* Remove */}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${pin.title}`}
        className="flex size-7 shrink-0 items-center justify-center rounded-md text-ds2026-text-secondary transition-colors hover:bg-ds2026-badge-error-bg hover:text-ds2026-badge-error-icon"
      >
        <PiX size={16} />
      </button>
    </li>
  )
}

// Owns a *draft* copy of the pins so edits are staged: "Apply changes" commits
// the draft to the page, "Cancel" throws it away. Rendered only while the modal
// is open (and remounted via `key`), so re-opening always starts from the
// currently-saved pins — no useEffect needed to reset.
function EditPinsContent({
  pins,
  onApply,
  onCancel,
}: {
  pins: PinItem[]
  onApply: (next: PinItem[]) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState<PinItem[]>(pins)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setDraft((prev) => {
        const oldIndex = prev.findIndex((p) => p.id === active.id)
        const newIndex = prev.findIndex((p) => p.id === over.id)
        return arrayMove(prev, oldIndex, newIndex)
      })
    }
  }

  return (
    <div className="flex flex-col">
      {draft.length === 0 ? (
        <p className="py-10 text-center text-sm text-ds2026-text-secondary">
          You haven&apos;t pinned anything yet.
        </p>
      ) : (
        <div className="ds-modal-scroll max-h-[360px] overflow-y-auto pr-1">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={draft.map((p) => p.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="flex flex-col gap-1.5">
                {draft.map((pin) => (
                  <SortablePinRow
                    key={pin.id}
                    pin={pin}
                    onRemove={() =>
                      setDraft((prev) => prev.filter((p) => p.id !== pin.id))
                    }
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* ds2026 footer buttons — smaller, brighter green primary */}
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-ds2026-btn-outline-stroke bg-ds2026-btn-outline-bg px-3.5 py-2 text-sm font-medium text-ds2026-btn-outline-text transition-colors hover:bg-ds2026-btn-outline-hover-bg"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onApply(draft)}
          className="rounded-lg bg-ds2026-btn-primary-bg px-3.5 py-2 text-sm font-semibold text-ds2026-btn-primary-text transition-colors hover:bg-ds2026-btn-primary-hover-bg"
        >
          Apply changes
        </button>
      </div>
    </div>
  )
}

function EditPinsModal({
  open,
  onOpenChange,
  pins,
  onApply,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  pins: PinItem[]
  onApply: (next: PinItem[]) => void
}) {
  return (
    <FbModal
      open={open}
      onOpenChange={onOpenChange}
      title="Edit pins"
      description="Drag to reorder, or remove pins from your homepage."
      size="md"
      hideCancelButton
    >
      <EditPinsContent
        key={String(open)}
        pins={pins}
        onCancel={() => onOpenChange(false)}
        onApply={(next) => {
          onApply(next)
          onOpenChange(false)
        }}
      />
    </FbModal>
  )
}

// ─── The card ────────────────────────────────────────────────────────────────

export function QuickAccessCard() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [editOpen, setEditOpen] = useState(false)
  const [reminderOpen, setReminderOpen] = useState(false)
  // contact/note/document pins open a popup card; record/page pins deep-link.
  const [openCard, setOpenCard] = useState<PinItem | null>(null)

  // ── Data ───────────────────────────────────────────────────────────────
  const { data: apiPins, isLoading: pinsLoading } = useListHomepagePins()
  const { data: apiRecents, isLoading: recentsLoading } =
    useListHomepageRecents()
  // Upcoming = this user's active reminders that haven't fired yet, soonest
  // first, capped at a small page so the card stays compact.
  // Stable at mount — recomputing inline produces a new queryKey every render,
  // which refetches forever.
  const scheduledAfter = useMemo(() => new Date().toISOString(), [])
  const { data: apiReminders, isLoading: remindersLoading } = useListReminders(
    {
      recipient: user?.id ? [user.id] : undefined,
      is_active: true,
      scheduled_after: scheduledAfter,
      ordering: 'next_run_at',
      page_size: 5,
    },
    { query: { enabled: Boolean(user?.id) } }
  )

  const reorderPins = useReorderHomepagePins()
  const deletePin = useDeleteHomepagePin()

  // Pins render straight off the server list (derived, not stored). Reorder /
  // remove flow through mutations that invalidate + refetch, so the displayed
  // order always reflects the persisted order. The Edit-pins modal owns its own
  // draft copy for in-flight drag smoothness (see EditPinsContent).
  const pins: PinItem[] = (apiPins ?? []).map(pinFromApi)
  const recents: RecentItem[] = (apiRecents ?? [])
    .map(recentFromApi)
    .filter((r): r is RecentItem => r !== null)
  const upcoming: ReminderItem[] = (apiReminders?.results ?? []).map(
    upcomingFromApi
  )

  const loading = pinsLoading || recentsLoading || remindersLoading
  const allEmpty =
    !loading &&
    pins.length === 0 &&
    recents.length === 0 &&
    upcoming.length === 0

  function handlePinClick(pin: PinItem) {
    if (
      pin.type === 'contact' ||
      pin.type === 'note' ||
      pin.type === 'document'
    ) {
      // No standalone page for these — open the detail popup card instead.
      setOpenCard(pin)
      return
    }
    // company/deal/campaign (and page pins) deep-link to their full page.
    if (pin.href) {
      navigate(pin.href)
    }
  }

  // Persist a reordered / pruned pin set. Drag order + removals from the Edit
  // modal both funnel through here. Removed pins are deleted individually;
  // the surviving set is reordered with its full id sequence. Both mutations
  // invalidate the pins query, so the card re-renders from the fresh server
  // list — no local state to keep in sync.
  async function handlePinsChange(next: PinItem[]): Promise<void> {
    const removed = pins.filter((p) => !next.some((n) => n.id === p.id))
    try {
      await Promise.all(
        removed.map((p) => deletePin.mutateAsync({ id: String(p.id) }))
      )
      if (next.length > 0) {
        await reorderPins.mutateAsync({
          data: { pin_ids: next.map((p) => Number(p.id)) },
        })
      }
    } catch {
      toast.error('Failed to update pins. Please try again.')
    }
  }

  // While the first fetch is in flight render nothing — the card pops in once
  // there's real content (or collapses to the compact empty row when there
  // genuinely is none).
  if (loading) {
    return null
  }

  return (
    <section className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      {allEmpty ? (
        <AllEmptyRow />
      ) : (
        <>
          {/* Pins — own row, horizontal scroll */}
          <SectionHeader
            icon={PiPushPin}
            title="Pinned"
            menu={
              pins.length === 0 ? (
                <EmptyLabel text="No pins yet" />
              ) : (
                <ActionsDropdown
                  triggerIcon={Ellipsis}
                  triggerClassName={MENU_TRIGGER_CLASS}
                  contentClassName={MENU_CONTENT_CLASS}
                  actions={[
                    {
                      label: 'Edit pins',
                      icon: PiPencilSimple,
                      onClick: () => setEditOpen(true),
                    },
                  ]}
                />
              )
            }
          />
          {pins.length > 0 && (
            <div className="ds-modal-scroll -mx-1 flex gap-3 overflow-x-auto px-1 pb-1">
              {pins.map((pin) => (
                <PinCard
                  key={pin.id}
                  pin={pin}
                  onClick={() => handlePinClick(pin)}
                />
              ))}
            </div>
          )}

          <div className="my-4 border-t border-ds2026-border-primary" />

          {/* Recents + Upcoming share a row */}
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-2">
            <div className="min-w-0">
              <SectionHeader
                icon={PiClockCounterClockwise}
                title="Recently visited"
              />
              {recents.length === 0 ? (
                <p className="px-1 py-3 text-xs text-ds2026-text-secondary">
                  Nothing here yet
                </p>
              ) : (
                <div className="ds-modal-scroll max-h-[168px] overflow-y-auto pr-1">
                  {recents.map((item) => (
                    <RecentRow
                      key={item.id}
                      item={item}
                      onClick={() => item.href && navigate(item.href)}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="min-w-0">
              <SectionHeader
                icon={PiBellRinging}
                title="Upcoming"
                menu={
                  upcoming.length > 0 ? (
                    <ActionsDropdown
                      // -my-1.5 keeps the 32px tap target but stops it from
                      // inflating the header, so Upcoming's header matches the
                      // text-height "Recently visited" header beside it.
                      className="-my-1.5"
                      triggerIcon={Ellipsis}
                      triggerClassName={MENU_TRIGGER_CLASS}
                      contentClassName={MENU_CONTENT_CLASS}
                      actions={[
                        {
                          label: 'Schedule reminder',
                          icon: PiCalendarPlus,
                          onClick: () => setReminderOpen(true),
                        },
                        {
                          label: 'View reminders',
                          icon: PiListBullets,
                          onClick: () => navigate('/reminders'),
                        },
                      ]}
                    />
                  ) : undefined
                }
              />
              {upcoming.length === 0 ? (
                <p className="px-1 py-3 text-xs text-ds2026-text-secondary">
                  Nothing here yet
                </p>
              ) : (
                <div className="ds-modal-scroll max-h-[168px] overflow-y-auto pr-1">
                  {upcoming.map((reminder) => (
                    <UpcomingRow key={reminder.id} reminder={reminder} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <EditPinsModal
        open={editOpen}
        onOpenChange={setEditOpen}
        pins={pins}
        onApply={handlePinsChange}
      />

      <ReminderModal
        open={reminderOpen}
        onClose={() => setReminderOpen(false)}
      />

      <PinCardPopup pin={openCard} onClose={() => setOpenCard(null)} />
    </section>
  )
}
